import { create } from 'zustand';
import type { NavigationItemDto } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { translate, type MessageKey } from '@/i18n/messages';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * The shop's header menu (docs/07-Admin/Admin-App.md, "Header menu"), read and
 * saved whole — the API takes the menu as one list, so there is nothing to save
 * an item at a time. The page edits a copy and hands it back here.
 */
interface NavigationState {
  /** The menu as last read or saved; null until it has been read. */
  items: NavigationItemDto[] | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  load: () => Promise<void>;
  save: (items: NavigationItemDto[]) => Promise<boolean>;
}

export const useNavigationStore = create<NavigationState>((set) => ({
  items: null,
  loading: false,
  saving: false,
  error: null,

  async load() {
    set({ loading: true, error: null });
    try {
      const menu = await withSession(() => adminApi.navigation.get());
      set({ items: menu.items, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, say('menuLoadFailed')) });
    }
  },

  async save(items) {
    set({ saving: true, error: null });
    try {
      const menu = await withSession(() => adminApi.navigation.update(items));
      set({ items: menu.items, saving: false });
      return true;
    } catch (error) {
      // The API names the offending item; its message beats a generic one.
      set({ saving: false, error: errorMessage(error, say('menuSaveFailed')) });
      return false;
    }
  },
}));

/** A fallback message in the operator's language, for a failure the API did not word. */
function say(key: MessageKey): string {
  return translate(key, useUiStore.getState().locale);
}
